import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useCommentMessages } from '@/shared/hooks/useCommentMessages';
import { useAuthUnified } from '@/domains/billing/hooks/shared/useAuthUnified';
import { useUserResolution } from '@/shared/hooks/useUserResolution';
import { useSystemUsers } from '@/shared/hooks/useSystemUsers';
import { ChatMessageService } from '@/shared/services/comments/chatMessageService';
import { log } from '@/lib/logger';
import { cn } from '@/lib/utils';
import {
  Send,
  X,
  Check,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { StructuredMentionInput, type StructuredMentionInputHandle } from './StructuredMentionInput';
import { EmojiReactions, type Reaction } from './EmojiReactions';
import { StructuredMessageRenderer } from './StructuredMessageRenderer';
import type { EmployeeData } from '@shared/types/system-users';
import { avatarCacheHelpers } from '@shared/types/system-users';
import type { Timestamp } from '@shared/types/submission';
import type { ContentBlock } from './types/messageContent';
import { serializeContent, deserializeContent, toPlainText } from './types/messageContent';
import type { ChatMessage } from '@/shared/stores/commentStore';

interface CommentModalProps {
  channelId: string;
  entryId: string;
  workflowId: string;
  workflowName?: string;
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
  className?: string;
  onResolve?: () => void;
  onDelete?: () => void;
  pinContext?: {
    pageUrl: string;
    scrollY: number;
    pinId: string;
  };
  useEnhancedUI?: boolean;
}

export const CommentModal: React.FC<CommentModalProps> = ({
  channelId,
  entryId,
  workflowId,
  isOpen,
  onClose,
  position,
  className,
  onResolve,
  onDelete,
  pinContext,
  useEnhancedUI = false,
}) => {
  const { effectiveUser } = useAuthUnified();
  const { users } = useSystemUsers();

  const draftKey = `comment-draft-${entryId}`;

  const [messageBlocks, setMessageBlocks] = useState<ContentBlock[]>(() => {
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        return JSON.parse(savedDraft) as ContentBlock[];
      } catch {
        return [];
      }
    }
    return [];
  });
  const [_currentMentions, setCurrentMentions] = useState<string[]>(() => {
    const savedMentions = localStorage.getItem(`${draftKey}-mentions`);
    return savedMentions ? JSON.parse(savedMentions) : [];
  });
  const [isSending, setIsSending] = useState(false);
  const [isResolved, setIsResolved] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const mentionInputRef = useRef<StructuredMentionInputHandle>(null);

  const {
    messages,
    isConnected,
    isLoading,
    isSending: hookIsSending,
    error,
    sendMessage,
    resolveConversation,
  } = useCommentMessages(channelId, entryId, workflowId, isOpen);

  const [localError, setLocalError] = useState<string | null>(null);
  const displayError = error || localError;
  const clearError = () => setLocalError(null);

  const { resolveUserName } = useUserResolution();

  useEffect(() => {
    if (!isOpen) return;

    const MODAL_WIDTH = useEnhancedUI ? 480 : 320;
    const MODAL_MAX_HEIGHT = useEnhancedUI ? 576 : 384;
    const SPACING = 10;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedTop = position.top;
    let adjustedLeft = position.left - MODAL_WIDTH;

    if (adjustedLeft < SPACING) {
      adjustedLeft = position.left + SPACING;
    }

    if (adjustedLeft + MODAL_WIDTH > viewportWidth - SPACING) {
      adjustedLeft = viewportWidth - MODAL_WIDTH - SPACING;
    }

    const modalBottom = adjustedTop + MODAL_MAX_HEIGHT;
    if (modalBottom > viewportHeight - SPACING) {
      adjustedTop = position.top - MODAL_MAX_HEIGHT - SPACING;

      if (adjustedTop < SPACING) {
        adjustedTop = SPACING;
      }
    }

    setAdjustedPosition({ top: adjustedTop, left: adjustedLeft });
  }, [position, isOpen, useEnhancedUI]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const isEmojiPicker = target.closest('.EmojiPickerReact') ||
                           target.closest('[class*="epr-"]') ||
                           target.closest('aside[class*="EmojiPicker"]');

      const isQuickReactions = target.closest('[class*="bg-white"][class*="border"][class*="rounded-lg"][class*="shadow-xl"]');

      if (modalRef.current && !modalRef.current.contains(event.target as Node) && !isEmojiPicker && !isQuickReactions) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleBeforeUnload = () => {
      if (isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isOpen, onClose]);

  const handleSendMessage = async (blocks: ContentBlock[], mentions: string[]) => {
    const plainText = toPlainText(blocks);
    if (!plainText.trim() || isSending) {
      return;
    }

    try {
      setIsSending(true);
      const serializedText = serializeContent(blocks);
      await sendMessage(serializedText, mentions, pinContext);

      setMessageBlocks([]);
      setCurrentMentions([]);
      localStorage.removeItem(draftKey);
      localStorage.removeItem(`${draftKey}-mentions`);
    } catch (err) {
      log.error('Error sending message:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageChange = (blocks: ContentBlock[], mentions: string[]) => {
    setMessageBlocks(blocks);
    setCurrentMentions(mentions);

    if (blocks.length > 0) {
      localStorage.setItem(draftKey, JSON.stringify(blocks));
      localStorage.setItem(`${draftKey}-mentions`, JSON.stringify(mentions));
    } else {
      localStorage.removeItem(draftKey);
      localStorage.removeItem(`${draftKey}-mentions`);
    }
  };

  const handleMarkAsResolved = async () => {
    try {
      setIsResolved(true);

      if (onResolve) {
        onResolve();
      } else {
        await resolveConversation();
        onClose();
      }

      setTimeout(() => {
        setIsResolved(false);
      }, 100);

    } catch (error) {
      log.error('[Chat Modal] Error resolving comment:', error);
      setIsResolved(false);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const handleAddReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!effectiveUser?.employee_id) {
      return;
    }

    try {
      const clientId = effectiveUser.employee_id.toString();
      await ChatMessageService.addReaction(messageId, emoji, clientId);
    } catch (error) {
      log.error('[Chat Modal] Error adding reaction:', error);
    }
  }, [effectiveUser]);

  const handleRemoveReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!effectiveUser?.employee_id) {
      return;
    }

    try {
      const clientId = effectiveUser.employee_id.toString();
      await ChatMessageService.removeReaction(messageId, emoji, clientId);
    } catch (error) {
      log.error('[Chat Modal] Error removing reaction:', error);
    }
  }, [effectiveUser]);

  const getMessageReactions = useCallback((messageId: string): Reaction[] => {
    const message = messages.find(m => m.id === messageId);
    if (!message?.reactions) {
      return [];
    }

    const reactionsList: Reaction[] = [];

    Object.entries(message.reactions).forEach(([emoji, clientIds]) => {
      if (clientIds && clientIds.length > 0) {
        const userNames = clientIds.map(clientId => resolveUserName(clientId));

        reactionsList.push({
          emoji,
          users: userNames,
          count: clientIds.length
        });
      }
    });

    return reactionsList;
  }, [messages, resolveUserName]);

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getUserAvatar = useCallback((message: ChatMessage) => {
    const resolvedUserName = resolveUserName(message.clientId);

    const user = users.find(u => u.employee_id.toString() === message.clientId.toString());
    const employeeData: EmployeeData | undefined = user?.employee_data ? {
      name: user.employee_data.name,
      avatar: user.employee_data.avatar,
      cached_avatar_url: user.employee_data.cached_avatar_url,
      cached_thumbnail_url: user.employee_data.cached_thumbnail_url,
      avatar_cache_status: user.employee_data.avatar_cache_status,
      avatar_cache_timestamp: user.employee_data.avatar_cache_timestamp instanceof Date
        ? user.employee_data.avatar_cache_timestamp
        : user.employee_data.avatar_cache_timestamp
          ? new Date((user.employee_data.avatar_cache_timestamp as Timestamp).seconds * 1000)
          : undefined,
    } : undefined;

    let avatarUrl: string | undefined = undefined;

    if (employeeData) {
      if (employeeData.cached_thumbnail_url && employeeData.avatar_cache_status === 'cached') {
        const isValid = !employeeData.avatar_cache_timestamp ||
          avatarCacheHelpers.isCacheValid(employeeData.avatar_cache_timestamp, 168);

        if (isValid) {
          avatarUrl = employeeData.cached_thumbnail_url;
        }
      }

      if (!avatarUrl && employeeData.cached_avatar_url && employeeData.avatar_cache_status === 'cached') {
        const isValid = !employeeData.avatar_cache_timestamp ||
          avatarCacheHelpers.isCacheValid(employeeData.avatar_cache_timestamp, 168);

        if (isValid) {
          avatarUrl = employeeData.cached_avatar_url;
        }
      }
    }

    if (avatarUrl) {
      return (
        <img
          key={`avatar-${message.clientId}`}
          src={avatarUrl}
          alt={resolvedUserName}
          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      );
    }

    const userInitials = resolvedUserName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

    return (
      <div
        key={`avatar-${message.clientId}`}
        className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
      >
        {userInitials}
      </div>
    );
  }, [users, resolveUserName]);

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className={cn(
        'fixed z-50 bg-white border border-gray-300 rounded-lg shadow-xl',
        'flex flex-col',
        useEnhancedUI ? 'w-[480px] max-h-[576px]' : 'w-80 max-h-96',
        className
      )}
      style={{
        top: adjustedPosition.top,
        left: adjustedPosition.left
      }}
    >
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center space-x-2">
          {onDelete && (
            <button
              onClick={handleDelete}
              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
              title="Delete comment"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {messages.length > 0 && !isResolved && (
            <button
              onClick={handleMarkAsResolved}
              className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
              title="Mark as resolved"
            >
              <Check className="h-4 w-4" />
            </button>
          )}

          {isResolved && (
            <div className="p-1.5 text-green-600" title="Resolved">
              <Check className="h-4 w-4" />
            </div>
          )}
        </div>

        <div className="flex items-center space-x-1">
          {isLoading ? (
            <Loader2 className="h-3 w-3 text-gray-400 animate-spin" />
          ) : !isConnected ? (
            <div className="h-2 w-2 bg-red-500 rounded-full" title="Disconnected" />
          ) : null}

          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {displayError && (
        <div className="p-2 bg-red-50 border-b border-red-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-3 w-3 text-red-600" />
            <span className="text-xs text-red-700">{displayError}</span>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800 text-xs underline ml-auto"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            <div className="text-sm">No comments yet</div>
            <div className="text-xs mt-1">Add a comment to start the conversation</div>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="flex items-start space-x-2">
              {getUserAvatar(message)}

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">
                      {resolveUserName(message.clientId)}
                    </span>
                    <span>•</span>
                    <span>{formatTime(message.createdAt)}</span>
                  </div>

                  {useEnhancedUI && effectiveUser?.employee_id && (
                    <EmojiReactions
                      messageId={message.id}
                      reactions={getMessageReactions(message.id)}
                      currentUserName={resolveUserName(effectiveUser.employee_id.toString())}
                      onAddReaction={handleAddReaction}
                      onRemoveReaction={handleRemoveReaction}
                    />
                  )}
                </div>
                <div className="text-sm text-gray-900 mt-1 break-words text-left">
                  <StructuredMessageRenderer
                    blocks={deserializeContent(message.text)}
                    currentUserEmail={effectiveUser?.email}
                  />
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-gray-200 p-3">
        <div className="relative">
          <StructuredMentionInput
            ref={mentionInputRef}
            value={messageBlocks}
            onChange={handleMessageChange}
            onSubmit={handleSendMessage}
            placeholder={useEnhancedUI ? "Write a comment..." : "Reply or add others with @"}
            disabled={isSending || hookIsSending || isResolved}
            className="text-sm"
          />
          {!useEnhancedUI && (
            <button
              type="button"
              onClick={() => mentionInputRef.current?.submit()}
              disabled={isSending || hookIsSending || isResolved}
              className="absolute right-1 bottom-2 w-7 h-7 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 m-1"
            >
              {isSending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

